import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Footer from '../components/Footer';

export default function WhySiteScope() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("push_registered_v1");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header - Conditional based on login status */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={token ? "/dashboard" : "/"} className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="/logo-icon.png" 
              alt="SiteScope" 
              className="w-12 h-12 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" 
            />
            <span className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          
          {/* Navigation - Different for logged in vs logged out */}
          {token ? (
            // Logged in navigation
            <div className="flex items-center gap-3 sm:gap-4">
              <Link 
                to="/dashboard" 
                className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors hidden sm:block"
              >
                Dashboard
              </Link>
              <Link 
                to="/user-pricing" 
                className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors hidden sm:block"
              >
                Upgrade
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400 hidden md:block">
                  {user?.email}
                </span>
                <Button 
                  onClick={handleLogout} 
                  variant="ghost" 
                  size="sm" 
                  light
                  className="text-sm sm:text-base px-3 sm:px-4 py-2 font-medium"
                >
                  Logout
                </Button>
              </div>
            </div>
          ) : (
            // Public navigation
            <div className="flex items-center gap-3 sm:gap-4">
              <Link 
                to="/pricing" 
                className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors hidden sm:block"
              >
                Pricing
              </Link>
              <Button as={Link} to="/login" variant="ghost" size="sm" light className="text-sm sm:text-base px-3 sm:px-4 py-2 font-medium">
                Sign in
              </Button>
              <Button as={Link} to="/register" size="sm" light className="text-sm sm:text-base px-4 sm:px-6 py-2 shadow-lg font-semibold">
                Start Free
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 py-12 sm:py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-block bg-blue-100 dark:bg-blue-900/30 px-4 py-2 rounded-full mb-6">
            <span className="text-sm sm:text-base font-semibold text-blue-600 dark:text-blue-400">
              For Agencies & Enterprises
            </span>
          </div>
          
          <h1 className="text-[2.5rem] sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6 leading-tight">
            Why Your Business Needs
            <span className="text-blue-600 dark:text-blue-400 block sm:inline"> SiteScope</span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-10 leading-relaxed">
            Protect your reputation, save time, and ensure your clients' websites are always online with enterprise-grade monitoring.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            {token ? (
              // Logged in - show Dashboard and Upgrade buttons
              <>
                <Button as={Link} to="/dashboard" size="xl" light className="px-8 py-4 text-lg font-semibold shadow-xl">
                  📊 Go to Dashboard
                </Button>
                <Button as={Link} to="/user-pricing" variant="outline" size="xl" light className="px-8 py-4 text-lg font-semibold border-2">
                  ⬆️ Upgrade Plan
                </Button>
              </>
            ) : (
              // Logged out - show registration and pricing
              <>
                <Button as={Link} to="/register" size="xl" light className="px-8 py-4 text-lg font-semibold shadow-xl">
                  🚀 Start Free Trial
                </Button>
                <Button as={Link} to="/pricing" variant="outline" size="xl" light className="px-8 py-4 text-lg font-semibold border-2">
                  View Pricing
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* The Cost of Downtime */}
      <section className="bg-white dark:bg-gray-800 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              The Real Cost of Website Downtime
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Every minute your website is down costs you money and trust
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-red-50 dark:bg-red-900/20 p-8 rounded-xl border-2 border-red-200 dark:border-red-800 text-center">
              <div className="text-5xl mb-4">💸</div>
              <h3 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-3">Lost Revenue</h3>
              <p className="text-gray-700 dark:text-gray-300">
                E-commerce sites lose an average of <strong>$5,600 per minute</strong> of downtime
              </p>
            </div>
            
            <div className="bg-orange-50 dark:bg-orange-900/20 p-8 rounded-xl border-2 border-orange-200 dark:border-orange-800 text-center">
              <div className="text-5xl mb-4">😤</div>
              <h3 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-3">Customer Trust</h3>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>79% of customers</strong> won't return to a site that performed poorly
              </p>
            </div>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-8 rounded-xl border-2 border-yellow-200 dark:border-yellow-800 text-center">
              <div className="text-5xl mb-4">📉</div>
              <h3 className="text-2xl font-bold text-yellow-600 dark:text-yellow-500 mb-3">SEO Ranking</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Frequent downtime <strong>damages your SEO rankings</strong> and organic traffic
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Perfect For
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Trusted by agencies and enterprises worldwide
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Digital Agencies */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center mb-6">
                <span className="text-4xl">🎨</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Digital Agencies</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Manage all your clients' websites from one dashboard. Impress clients with proactive monitoring and instant issue resolution.
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Monitor 200+ client sites</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>White-label reporting</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Team collaboration</span>
                </li>
              </ul>
            </div>
            
            {/* E-commerce Businesses */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center mb-6">
                <span className="text-4xl">🛒</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">E-commerce</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Protect your revenue with 24/7 monitoring. Know immediately when checkout is down or performance degrades.
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Transaction monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Performance tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>SSL certificate alerts</span>
                </li>
              </ul>
            </div>
            
            {/* SaaS Companies */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mb-6">
                <span className="text-4xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">SaaS Companies</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Maintain your SLA commitments with comprehensive uptime monitoring and detailed reporting.
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>99.9% uptime tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>API endpoint monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Status page integration</span>
                </li>
              </ul>
            </div>
            
            {/* Marketing Teams */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900 rounded-xl flex items-center justify-center mb-6">
                <span className="text-4xl">📢</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Marketing Teams</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Ensure campaign landing pages are always available. Monitor high-traffic events without stress.
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Campaign page monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Load time tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Multi-location checks</span>
                </li>
              </ul>
            </div>
            
            {/* Web Hosting */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-xl flex items-center justify-center mb-6">
                <span className="text-4xl">🖥️</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Web Hosting</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Offer monitoring as a value-add service to your hosting clients. Build trust and reduce support tickets.
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Server health monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>DNS monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Resource tracking</span>
                </li>
              </ul>
            </div>
            
            {/* Enterprises */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-xl flex items-center justify-center mb-6">
                <span className="text-4xl">🏢</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Enterprise</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Scale monitoring across global infrastructure with enterprise-grade features and dedicated support.
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Unlimited monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Custom integrations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>Dedicated support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-white dark:bg-gray-800 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Why Teams Choose SiteScope
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Everything you need to keep your websites running smoothly
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⚡</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Instant Notifications</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Get alerted within 30 seconds via email, SMS, and push notifications when issues are detected.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Detailed Analytics</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Track uptime percentage, response times, and historical performance with beautiful dashboards.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔒</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">SSL Monitoring</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Never let SSL certificates expire. Get reminded 30, 14, and 7 days before expiration.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🌍</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Global Monitoring</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Check your websites from multiple locations worldwide to ensure global availability.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">👥</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Team Collaboration</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Share access with team members, assign responsibilities, and collaborate on incident resolution.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔧</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Easy Setup</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  No complex configuration. Just enter your URL and start monitoring in seconds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-2xl p-12 text-white">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Trusted by Teams Worldwide
              </h2>
              <p className="text-xl text-blue-100">
                Join thousands of agencies and enterprises monitoring with SiteScope
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold mb-2">10K+</div>
                <div className="text-blue-100">Websites Monitored</div>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">99.9%</div>
                <div className="text-blue-100">Uptime Accuracy</div>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">&lt;30s</div>
                <div className="text-blue-100">Alert Speed</div>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">24/7</div>
                <div className="text-blue-100">Monitoring</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            {token ? "Ready to Scale Your Monitoring?" : "Start Monitoring in Minutes"}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            {token 
              ? "Upgrade your plan to monitor more sites and get advanced features." 
              : "No credit card required. Start free and upgrade as you grow."
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {token ? (
              // Logged in - show Dashboard and Upgrade
              <>
                <Button as={Link} to="/dashboard" size="xl" light className="px-10 py-4 text-lg font-semibold shadow-xl">
                  📊 Go to Dashboard
                </Button>
                <Button as={Link} to="/user-pricing" variant="outline" size="xl" light className="px-10 py-4 text-lg font-semibold border-2">
                  ⬆️ Upgrade Plan
                </Button>
              </>
            ) : (
              // Logged out - show registration and pricing
              <>
                <Button as={Link} to="/register" size="xl" light className="px-10 py-4 text-lg font-semibold shadow-xl">
                  🚀 Start Free Trial
                </Button>
                <Button as={Link} to="/pricing" variant="outline" size="xl" light className="px-10 py-4 text-lg font-semibold border-2">
                  View Pricing Plans
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
